import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatUserName } from "../../utils/helpers";
import { useLocalization } from "../../context/LocalizationContext";
import { usePodIP } from "../../hooks/usePodIP";
import { fetchPodSchedule, endSession } from "../../utils/api";
import { isPodAvailable } from "../../utils/helpers";
import { useAppContext } from "../../context/AppContext";
import { useUserData } from "../../hooks/useUserData";
import { useAuth } from "../../context/AuthContext";
import Modal from "../shared/Modal";
import "./Dashboard.css";
import ActivityIcon from "../../assets/icons/activity.svg";
import VideoRecorderIcon from "../../assets/icons/video-recorder.svg";
import FileIcon from "../../assets/icons/file.svg";
import ChatIcon from "../../assets/icons/chat.svg";
import BookIcon from "../../assets/icons/book.svg";
import EndIcon from "../../assets/icons/end.svg";
import Header from "../shared/Header/index";
import LanguageToggle from "../shared/LanguageToggle";

const Dashboard = ({ userData }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { podIP } = usePodIP();
  const { savedAnswers, clearAnswers, resetState } = useAppContext();
  const { userData: userDataFromHook } = useUserData();
  const { logout } = useAuth();
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const logoutInitiated = useRef(false);

  // Button configuration with localized labels
  const BUTTON_CONFIG = [
    {
      id: "vitals",
      icon: ActivityIcon,
      label: t("dashboard.checkVitals"),
      ariaLabel: t("dashboard.checkVitals"),
      action: "navigate",
      route: "/vitals",
    },
    {
      id: "consultation",
      icon: VideoRecorderIcon,
      label: t("dashboard.joinConsultation"),
      ariaLabel: t("dashboard.joinConsultation"),
      action: "navigate",
      route: "/consultation",
    },
    {
      id: "assessment",
      icon: FileIcon,
      label: t("dashboard.healthAssessment"),
      ariaLabel: t("dashboard.healthAssessment"),
      action: "navigate",
      route: "/assessment",
    },
    {
      id: "chat",
      icon: ChatIcon,
      label: t("dashboard.chatManager"),
      ariaLabel: t("dashboard.chatManager"),
      action: "navigate",
      route: "/chat-manager",
    },
    {
      id: "knowledge",
      icon: BookIcon,
      label: t("dashboard.knowledgeBank"),
      ariaLabel: t("dashboard.knowledgeBank"),
      action: "placeholder",
      message: t("dashboard.knowledgeBank") + " clicked",
    },
    {
      id: "end",
      icon: EndIcon,
      label: t("dashboard.endSession"),
      ariaLabel: t("dashboard.endSession"),
      action: "endSession",
    },
  ];

  // Filter buttons based on pod IP
  const getFilteredButtons = () => {
    if (podIP == "192.168.1.200") {
      // Show all 6 buttons
      return BUTTON_CONFIG;
    } else {
      // Show only 4 buttons: vitals, consultation, assessment, end
      return BUTTON_CONFIG.filter((config) =>
        ["vitals", "consultation", "assessment", "end"].includes(config.id)
      );
    }
  };

  const handleEndSession = async (e) => {
    // Prevent default and stop propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent multiple clicks using ref (more reliable than state)
    if (logoutInitiated.current || isEnding) {
      return;
    }

    // Mark as initiated immediately
    logoutInitiated.current = true;
    setIsEnding(true);

    try {
      const sessionId = (userData || userDataFromHook)?.sessionId || 123;
      // Wait for session to end via API
      await endSession("192.168.1.100", sessionId);

      // Clear saved answers and reset state
      Object.keys(savedAnswers).forEach((assessmentId) => {
        clearAnswers(assessmentId);
      });
      resetState();

      // Logout immediately - this will redirect
      logout();
    } catch (error) {
      console.error('Error in handleEndSession:', error);
      // Clear state and logout even on error
      Object.keys(savedAnswers).forEach((assessmentId) => {
        clearAnswers(assessmentId);
      });
      resetState();
      logout();
    }
  };

  const handleButtonClick = async (config) => {
    if (config.action === "navigate") {
      // Check availability before navigating to consultation
      if (config.id === "consultation") {
        await handleConsultationClick();
      } else {
        navigate(config.route);
      }
    } else if (config.action === "endSession") {
      handleEndSession();
    } else if (config.action === "placeholder") {
      console.log(config.message);
    }
  };

  const handleConsultationClick = async () => {
    try {
      setIsCheckingAvailability(true);
      // Fetch pod schedule (using podId = 1 as default, can be made configurable)
      const scheduleData = await fetchPodSchedule(1, 4, 1, 1);

      // Check if pod is currently available
      const available = isPodAvailable(scheduleData);

      if (available) {
        // Navigate to consultation if available
        navigate("/consultation");
      } else {
        // Show modal if not available
        setShowUnavailableModal(true);
      }
    } catch (error) {
      console.error("Error checking pod availability:", error);
      // On error, show modal to inform user
      setShowUnavailableModal(true);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  return (
    <div className="main-container">
      <Header
        text={t("dashboard.welcomeMessage", {
          name: formatUserName(userData?.name),
        })}
      />
      <div className="dashboard-content">
        <div className="action-buttons">
          {getFilteredButtons().map((config) => (
            <button
              key={config.id}
              className="action-btn"
              onClick={() => handleButtonClick(config)}
              aria-label={config.ariaLabel}
              disabled={(config.id === "consultation" && isCheckingAvailability) || (config.id === "end" && isEnding)}
            >
              <img src={config.icon} alt={config.label} className="btn-icon" />
              <span>
                {config.id === "consultation" && isCheckingAvailability
                  ? t("consultation.checkingAvailability")
                  : config.label}
              </span>
            </button>
          ))}
        </div>
        <div>
          <LanguageToggle />
        </div>
      </div>
      <Modal
        isOpen={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
        title={t("consultation.notAvailable")}
        message={t("consultation.notAvailableMessage")}
      />
    </div>
  );
};

export default Dashboard;
